import type { TResponse } from '@/services';
import { apis, getAccessToken } from '@/libs-service';

export const OPPORTUNITY_TRACKING_EVENTS = {
  applicationCreatedSuccess: 'js_application_created_success',
  applyFail: 'js_apply_fail',
  applyOpportunity: 'js_apply_opportunity',
  applyStart: 'js_apply_start',
  applySuccess: 'js_apply_success',
  askApplicationResultFail: 'js_ask_application_result_fail',
  askApplicationResultSuccess: 'js_ask_application_result_success',
  askResultButtonImpression: 'js_ask_result_button_impression',
  confirmAskApplicationResult: 'js_confirm_ask_application_result',
  filter: 'js_filter_opportunity',
  impression: 'js_opportunity_impression',
  markApplicationReplied: 'js_mark_application_replied',
  save: 'js_save_opportunity',
  search: 'js_search_opportunity',
  share: 'js_share_opportunity',
  viewApplicationConfirmation: 'js_view_application_confirmation',
  viewApplicationStatus: 'js_view_application_status',
  viewBottomSheet: 'js_view_bottom_sheet',
  viewDetail: 'js_view_opportunity_detail',
  viewResult: 'js_view_opportunity_result',
} as const;

export type OpportunityTrackingEventName =
  (typeof OPPORTUNITY_TRACKING_EVENTS)[keyof typeof OPPORTUNITY_TRACKING_EVENTS];
export type SearchMethod =
  | 'type'
  | 'suggestion_click'
  | 'keyword_recommendation'
  | 'history_click'
  | 'cv_upload'
  | 'via_deeplink';
export type TrackingInfo = Record<string, unknown>;

const APPLICATION_TRACKING_INFO_ENDPOINT =
  'jobseekers-info/external/api/v1/application';

let userInfoRequest: Promise<TrackingInfo | null> | null = null;
let userInfoRequestToken: string | null = null;
let userInfoRequestExpiresAt = 0;

type TrackingRequestCacheEntry = {
  expiresAt: number;
  request: Promise<TrackingInfo | null>;
};

const TRACKING_REQUEST_CACHE_TTL_MS = 5 * 60 * 1000;
const TRACKING_REQUEST_CACHE_MAX_ENTRIES = 100;
const opportunityInfoRequests = new Map<string, TrackingRequestCacheEntry>();
const applicationInfoRequests = new Map<string, TrackingRequestCacheEntry>();

function getCachedTrackingRequest(
  cache: Map<string, TrackingRequestCacheEntry>,
  key: string
): Promise<TrackingInfo | null> | undefined {
  const entry = cache.get(key);

  if (!entry) return undefined;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);

    return undefined;
  }

  // Refresh insertion order so the first key remains the least recently used.
  cache.delete(key);
  cache.set(key, entry);

  return entry.request;
}

function cacheTrackingRequest(
  cache: Map<string, TrackingRequestCacheEntry>,
  key: string,
  request: Promise<TrackingInfo | null>
) {
  cache.set(key, {
    expiresAt: Date.now() + TRACKING_REQUEST_CACHE_TTL_MS,
    request,
  });

  while (cache.size > TRACKING_REQUEST_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;

    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }

  void request.then(data => {
    if (!data && cache.get(key)?.request === request) {
      cache.delete(key);
    }
  });
}

export async function getTrackingUserInfo(): Promise<TrackingInfo | null> {
  const token = await getAccessToken();

  if (!token) {
    userInfoRequest = null;
    userInfoRequestToken = null;
    userInfoRequestExpiresAt = 0;

    return null;
  }

  if (
    userInfoRequest &&
    userInfoRequestToken === token &&
    userInfoRequestExpiresAt > Date.now()
  ) {
    return userInfoRequest;
  }

  const request = import('@/services/profile')
    .then(({ getProfileForPostHog }) => getProfileForPostHog(token))
    .catch(() => null);

  userInfoRequest = request;
  userInfoRequestToken = token;
  userInfoRequestExpiresAt = Date.now() + TRACKING_REQUEST_CACHE_TTL_MS;

  const data = await request;

  if (!data && userInfoRequest === request) {
    userInfoRequest = null;
    userInfoRequestToken = null;
    userInfoRequestExpiresAt = 0;
  }

  return data;
}

export function getOpportunityTrackingInfo(
  jobId: string
): Promise<TrackingInfo | null> {
  const cachedRequest = getCachedTrackingRequest(
    opportunityInfoRequests,
    jobId
  );

  if (cachedRequest) return cachedRequest;

  // This is a same-origin proxy on purpose: the server forwards auth when
  // present, while guest impression tracking avoids a no-store token lookup.
  const request = fetch(
    `/api/jobs/${encodeURIComponent(jobId)}/tracking-info`,
    { credentials: 'same-origin' }
  )
    .then(async response => {
      if (!response.ok) return null;

      const result = (await response.json()) as TResponse<TrackingInfo>;

      return result.data ?? null;
    })
    .catch(() => null);

  cacheTrackingRequest(opportunityInfoRequests, jobId, request);

  return request;
}

export function getApplicationTrackingInfo(
  applicationId: string
): Promise<TrackingInfo | null> {
  const cachedRequest = getCachedTrackingRequest(
    applicationInfoRequests,
    applicationId
  );

  if (cachedRequest) return cachedRequest;

  const request = apis.publicApi
    .get<TResponse<TrackingInfo>>(
      `/${APPLICATION_TRACKING_INFO_ENDPOINT}/${applicationId}/tracking-info`
    )
    .then(response => response.data ?? null)
    .catch(() => null);

  cacheTrackingRequest(applicationInfoRequests, applicationId, request);

  return request;
}
